import React, { useEffect, useState, useRef } from 'react'
import {
  Modal,
  Form,
  Input,
  Select,
  Icon,
  Button,
  Row,
  Col,
  List,
  Avatar,
  message
} from 'antd'
import LocationSelectionModal from '../../EntitySelectorList/components/LocationSelectionModal'
import CharacterSelectionModal from '../../EntitySelectorList/components/CharacterSelectionModal'
import CommentInput from '../../CommentInput'
import './AddShotModal.scss'

const { TextArea } = Input
const { Option } = Select

let id = 1 // for dynamic keys

const AddShotModal = ({
  visible,
  onCancel,
  onConfirm,
  isAddingScene,
  locationReference = {},
  locationMap = {},
  characterList = [],
  selectedCharacter = [],
  nextSceneIndex,
  nextShotIndex,
  outfitMap = {},
  outfitsReference = {},
  outfits = [],
  propMap = {},
  propsReference = {},
  form
}) => {
  // Normalize malformed props
  const normalizedCharacterList =
    characterList === 'null' ? [] : characterList || []
  const normalizedSelectedCharacters =
    selectedCharacter === 'null' ? [] : selectedCharacter || []
  const normalizedLocationReference =
    locationReference === 'null' ? {} : locationReference || {}
  const normalizedLocationMap = locationMap === 'null' ? {} : locationMap || {}
  const normalizedOutfitMap = outfitMap === 'null' ? {} : outfitMap || {}
  const normalizedOutfitsReference =
    outfitsReference === 'null' ? {} : outfitsReference || {}
  const normalizedOutfits = outfits === 'null' ? [] : outfits || []
  const normalizedPropMap = propMap === 'null' ? {} : propMap || {}
  const normalizedPropsReference =
    propsReference === 'null' ? {} : propsReference || {}

  const {
    getFieldDecorator,
    resetFields,
    validateFields,
    setFieldsValue,
    getFieldValue
  } = form
  const [selectedLocations, setSelectedLocations] = useState({}) // { shotKey: locationKey }

  // Full Selection Modal States
  const [locationModalVisible, setLocationModalVisible] = useState(false)
  const [charModalVisible, setCharModalVisible] = useState(false)
  const [activeShotKeyForModal, setActiveShotKeyForModal] = useState(null)
  const [activeFieldNameForModal, setActiveFieldNameForModal] = useState(null)

  useEffect(() => {
    if (visible) {
      resetFields()
      setSelectedLocations({})
      // Set initial shot key
      getFieldDecorator('keys', { initialValue: [0] })
    }
  }, [visible])

  const addShot = () => {
    const keys = getFieldValue('keys')
    const nextKeys = keys.concat(id++)
    setFieldsValue({ keys: nextKeys })
  }

  const removeShot = k => {
    const keys = getFieldValue('keys')
    if (keys.length === 1) return
    setFieldsValue({
      keys: keys.filter(key => key !== k)
    })
  }

  const handleConfirm = () => {
    validateFields((err, values) => {
      if (!err) {
        // Map the dynamic fields to a cleaner array structure
        const { keys, shots } = values
        const formattedShots = keys.map((key, index) => ({
          ...shots[key],
          shotTitle: isAddingScene
            ? `Shot ${index + 1}`
            : `Shot ${nextShotIndex}`
        }))
        onConfirm({
          ...values,
          sceneTitle: `Scene ${nextSceneIndex}`,
          shots: formattedShots
        })
      }
    })
  }

  const handleLocationSelect = (shotKey, locationKey) => {
    setSelectedLocations(prev => ({ ...prev, [shotKey]: locationKey }))
    setFieldsValue({ [`shots[${shotKey}].location`]: locationKey })
  }

  // Visual Modal Selection
  const handleVisualLocationSelect = key => {
    if (activeShotKeyForModal !== null && activeFieldNameForModal) {
      const fieldName = `shots[${activeShotKeyForModal}].${activeFieldNameForModal}`
      const currentValue = getFieldValue(fieldName) || ''
      const newTag = `__${key}__`
      setFieldsValue({ [fieldName]: `${newTag}, ${currentValue}` })
    }
    setLocationModalVisible(false)
    setActiveShotKeyForModal(null)
    setActiveFieldNameForModal(null)
  }

  const handleVisualCharacterSelect = name => {
    if (activeShotKeyForModal !== null && activeFieldNameForModal) {
      const fieldName = `shots[${activeShotKeyForModal}].${activeFieldNameForModal}`
      const currentValue = getFieldValue(fieldName) || ''

      const mapping = normalizedSelectedCharacters.find(
        m => m.current.replace(/^__|__$/g, '') === name
      )
      const finalTag = mapping ? mapping.original : `__${name}__`

      setFieldsValue({ [fieldName]: `${currentValue} ${finalTag}` })
    }
    setCharModalVisible(false)
    setActiveShotKeyForModal(null)
    setActiveFieldNameForModal(null)
  }

  const renderLocationPicker = shotKey => {
    const locations = Object.entries(normalizedLocationReference)
    if (locations.length === 0) {
      return <div className="no-locations-compact">No locations available.</div>
    }

    const currentSelected = selectedLocations[shotKey]

    return (
      <div className="location-grid-side">
        {locations.map(([key, value]) => {
          const url =
            typeof value === 'string'
              ? value
              : value?.uri || value?.url || value?.image || value?.src

          return (
            <div
              key={key}
              className={`location-item-mini ${
                currentSelected === key ? 'active' : ''
              }`}
              onClick={() => handleLocationSelect(shotKey, key)}
            >
              <div className="mini-thumb">
                {url ? <img src={url} alt={key} /> : <Icon type="picture" />}
              </div>
              <span className="mini-label">{key}</span>
            </div>
          )
        })}
      </div>
    )
  }

  // Tagging logic is now handled by CommentInput

  getFieldDecorator('keys', { initialValue: [0] })
  const keys = getFieldValue('keys') || [0]

  return (
    <>
      <Modal
        title={null}
        visible={visible}
        onCancel={onCancel}
        width={1100}
        className="add-shot-modal-polished"
        centered
        maskClosable={false}
        footer={[
          <Button key="back" onClick={onCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleConfirm}>
            {isAddingScene ? 'Create Scene' : 'Add Shot'}
          </Button>
        ]}
      >
        <div className="modal-inner">
          <h1 className="main-title">
            {isAddingScene ? 'ADD SCENE' : 'ADD SHOT'}
          </h1>

          <Form layout="vertical">
            {isAddingScene && (
              <div className="scene-details-container">
                <h2 className="section-subtitle">
                  <span className="title-suffix">Scene {nextSceneIndex}</span>
                </h2>
                <div className="scene-box">
                  <Form.Item label="SCENE DESCRIPTION">
                    {getFieldDecorator('sceneDescription', {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a description'
                        }
                      ]
                    })(
                      <TextArea
                        rows={2}
                        placeholder="Enter high-level scene context..."
                        style={{ minHeight: 60 }}
                      />
                    )}
                  </Form.Item>
                </div>
              </div>
            )}

            <div className="shots-outer-container">
              {isAddingScene && (
                <div className="shots-header">
                  <div className="shots-label-group">
                    <h2 className="section-subtitle">Shots</h2>
                    <p className="shots-hint">
                      (One shot is compulsory, but you can add multiple in one
                      go)
                    </p>
                  </div>
                  <Button className="add-shot-btn" onClick={addShot}>
                    + Add Shot
                  </Button>
                </div>
              )}

              {keys.map((k, index) => (
                <div
                  key={k}
                  className="shot-unit-container"
                  style={{ marginTop: index > 0 ? 32 : 0 }}
                >
                  <div
                    className="shot-unit-header"
                    style={{ marginBottom: 16 }}
                  >
                    <h3 className="shot-title-badge">
                      {isAddingScene
                        ? `Shot ${index + 1}`
                        : `Shot Details - Shot ${nextShotIndex}`}
                    </h3>
                    {isAddingScene && keys.length > 1 && (
                      <Icon
                        type="close-circle"
                        theme="filled"
                        className="remove-shot-icon"
                        onClick={() => removeShot(k)}
                      />
                    )}
                  </div>

                  <Row gutter={24} className="prompt-split-row">
                    <Col span={11}>
                      <div className="prompt-container image-prompt">
                        <div className="prompt-header">IMAGE PROMPT</div>
                        <div className="prompt-body">
                          <Form.Item label="Description Text">
                            {getFieldDecorator(`shots[${k}].imageDescription`, {
                              rules: [
                                {
                                  required: true,
                                  message: 'Image description required'
                                }
                              ]
                            })(
                              <CommentInput
                                placeholder="Type @ for characters, _ or # for locations..."
                                characterList={normalizedCharacterList}
                                locationReference={normalizedLocationReference}
                                selectedCharacter={normalizedSelectedCharacters}
                                locationMap={normalizedLocationMap}
                                outfitMap={normalizedOutfitMap}
                                outfitsReference={normalizedOutfitsReference}
                                outfits={normalizedOutfits}
                                propMap={normalizedPropMap}
                                propsReference={normalizedPropsReference}
                                onOpenCharacterSelector={() => {
                                  setActiveShotKeyForModal(k)
                                  setActiveFieldNameForModal('imageDescription')
                                  setCharModalVisible(true)
                                }}
                                onOpenLocationSelector={() => {
                                  setActiveShotKeyForModal(k)
                                  setActiveFieldNameForModal('imageDescription')
                                  setLocationModalVisible(true)
                                }}
                              />
                            )}
                          </Form.Item>

                          <Form.Item label="Location Selection" required>
                            {getFieldDecorator(`shots[${k}].location`, {
                              rules: [
                                { required: true, message: 'Select a location' }
                              ]
                            })(<Input style={{ display: 'none' }} />)}
                            {renderLocationPicker(k)}
                          </Form.Item>

                          <Form.Item label="Shot Perspective">
                            {getFieldDecorator(`shots[${k}].shotSize`, {
                              initialValue: 'Wide shot'
                            })(
                              <Select dropdownClassName="polished-dropdown">
                                <Option value="Wide shot">Wide Shot</Option>
                                <Option value="Medium shot">Medium Shot</Option>
                                <Option value="Close up">Close Up</Option>
                                <Option value="Extreme Close up">
                                  Extreme Close Up
                                </Option>
                                <Option value="Bird's eye view">
                                  Bird's Eye View
                                </Option>
                              </Select>
                            )}
                          </Form.Item>
                        </div>
                      </div>
                    </Col>

                    <Col span={13}>
                      <div className="prompt-container video-prompt">
                        <div className="prompt-header">VIDEO PROMPT</div>
                        <div className="prompt-body">
                          <Form.Item label="Description Text">
                            {getFieldDecorator(`shots[${k}].videoDescription`, {
                              rules: [
                                {
                                  required: true,
                                  message: 'Video description required'
                                }
                              ]
                            })(
                              <CommentInput
                                placeholder="Describe the motion/action in the scene..."
                                characterList={normalizedCharacterList}
                                locationReference={normalizedLocationReference}
                                selectedCharacter={normalizedSelectedCharacters}
                                locationMap={normalizedLocationMap}
                                outfitMap={normalizedOutfitMap}
                                outfitsReference={normalizedOutfitsReference}
                                outfits={normalizedOutfits}
                                propMap={normalizedPropMap}
                                propsReference={normalizedPropsReference}
                                onOpenCharacterSelector={() => {
                                  setActiveShotKeyForModal(k)
                                  setActiveFieldNameForModal('videoDescription')
                                  setCharModalVisible(true)
                                }}
                                onOpenLocationSelector={() => {
                                  setActiveShotKeyForModal(k)
                                  setActiveFieldNameForModal('videoDescription')
                                  setLocationModalVisible(true)
                                }}
                              />
                            )}
                          </Form.Item>

                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item label="Duration (S)">
                                {getFieldDecorator(`shots[${k}].duration`, {
                                  initialValue: 4
                                })(<Input type="number" />)}
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="Emotional Tone">
                                {getFieldDecorator(
                                  `shots[${k}].emotionalTone`,
                                  {
                                    initialValue: 'Neutral'
                                  }
                                )(
                                  <Select dropdownClassName="polished-dropdown">
                                    <Option value="Neutral">Neutral</Option>
                                    <Option value="Solitary">Solitary</Option>
                                    <Option value="Joyful">Joyful</Option>
                                    <Option value="Tense">Tense</Option>
                                    <Option value="Mysterious">
                                      Mysterious
                                    </Option>
                                    <Option value="Epic">Epic</Option>
                                  </Select>
                                )}
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item label="Camera Movement">
                                {getFieldDecorator(`shots[${k}].camera`, {
                                  initialValue: 'Tracking Side'
                                })(
                                  <Select dropdownClassName="polished-dropdown">
                                    <Option value="Static">Static</Option>
                                    <Option value="Tracking Side">
                                      Tracking Side
                                    </Option>
                                    <Option value="Tracking Front">
                                      Tracking Front
                                    </Option>
                                    <Option value="Pan Left/Right">
                                      Pan Left/Right
                                    </Option>
                                    <Option value="Tilt Up/Down">
                                      Tilt Up/Down
                                    </Option>
                                  </Select>
                                )}
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="Motion Speed">
                                {getFieldDecorator(`shots[${k}].cameraMotion`, {
                                  initialValue: 'Normal'
                                })(
                                  <Select dropdownClassName="polished-dropdown">
                                    <Option value="Normal">Normal</Option>
                                    <Option value="Slow Motion">
                                      Slow Motion
                                    </Option>
                                    <Option value="Time-lapse">
                                      Time-lapse
                                    </Option>
                                    <Option value="Fast Motion">
                                      Fast Motion
                                    </Option>
                                  </Select>
                                )}
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          </Form>
        </div>
      </Modal>

      <LocationSelectionModal
        visible={locationModalVisible}
        onCancel={() => {
          setLocationModalVisible(false)
          setActiveShotKeyForModal(null)
        }}
        onSelect={handleVisualLocationSelect}
        locationMap={normalizedLocationMap}
        locationReference={normalizedLocationReference}
      />
      <CharacterSelectionModal
        visible={charModalVisible}
        onCancel={() => {
          setCharModalVisible(false)
          setActiveShotKeyForModal(null)
        }}
        onSelect={handleVisualCharacterSelect}
        characterList={normalizedCharacterList.filter(c =>
          normalizedSelectedCharacters.some(
            m => (m.current || '').replace(/^__|__$/g, '') === c.name
          )
        )}
        selectedCharacter={normalizedSelectedCharacters}
      />
    </>
  )
}

export default Form.create()(AddShotModal)
